import React from "react";
import dayjs from "dayjs";
import { Modal } from "../../../ui/primitives/Modal";
import { Stack } from "../../../ui/primitives/Stack";
import { Text } from "../../../ui/primitives/Text";
import { EmptyState } from "../../../ui/primitives/EmptyState";
import { useTokens } from "../../../core/theme/TokensProvider";
import { MemberConversion } from "../../../model/members/MemberConversion";

export interface ConversionsModalProps {
  open: boolean;
  onClose: () => void;
  conversions: MemberConversion[];
  /** May exceed conversions.length when the server capped the list. */
  totalCount: number;
}

// Accepts a Date as well as a string: DateReviver revives ISO-looking response fields, so
// convertedAt arrives as either depending on the path it took.
const formatDate = (raw: string | Date): string => {
  const d = dayjs(raw);
  return d.isValid() ? d.format("DD/MM/YYYY") : "—";
};

/**
 * Who converted, by whom, and when (FORT-360).
 *
 * Every row here is a conversion that was recorded as it happened. Members converted before
 * the feature existed are deliberately absent — see MemberQueries.GetConversions for why
 * back-dating them from their membership start would have made the counter meaningless.
 */
const ConversionsModal: React.FC<ConversionsModalProps> = ({
  open,
  onClose,
  conversions,
  totalCount,
}) => {
  const tokens = useTokens();
  const capped = totalCount > conversions.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Membership conversions"
      footer={null}
      width={560}
      data-testid="conversions-modal"
    >
      {conversions.length === 0 ? (
        <EmptyState
          description="No conversions yet — a lead appears here once they are activated as a member."
          data-testid="conversions-modal-empty"
        />
      ) : (
        <Stack gap={0} style={{ maxHeight: 420, overflowY: "auto" }}>
          {capped ? (
            <Text variant="caption" color="secondary" data-testid="conversions-modal-capped">
              Showing the {conversions.length} most recent of {totalCount}.
            </Text>
          ) : null}
          {conversions.map((c) => (
            <div
              key={c.membershipId}
              data-testid={`conversion-row-${c.membershipId}`}
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: tokens.space[3],
                padding: `${tokens.space[2]}px 0`,
                borderBottom: `1px solid ${tokens.color.border.default}`,
              }}
            >
              <Stack gap={0}>
                <Text weight="semibold">{c.memberName}</Text>
                <Text variant="caption" color="secondary">
                  {c.convertedByStaffName
                    ? `Converted by ${c.convertedByStaffName}`
                    : "Converted by — not recorded"}
                </Text>
              </Stack>
              <Text>{formatDate(c.convertedAt)}</Text>
            </div>
          ))}
        </Stack>
      )}
    </Modal>
  );
};

export default ConversionsModal;
